import { useState, useEffect, useLayoutEffect, useCallback, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import type { LanguageCode } from '../types'
import type { TranslationStructure } from '../services/translations'
import { I18nContext } from './I18nContext'
import en from './en'
import { installTypography } from '../lib/typography'
import { cachedLanguage, loadLanguage, readPreferredLanguage } from './languageResources'
import { systemCopy } from './systemCopy'
import PublicRouteLoading from '../components/ui/PublicRouteLoading'
import { Button } from '../components/ui/Primitives'

interface I18nProviderProps {
    children: ReactNode
    defaultLanguage?: LanguageCode
}

const lookup = (strings: TranslationStructure, key: string): unknown => {
    let current: unknown = strings
    for (const part of key.split('.')) {
        if (!current || typeof current !== 'object' || !Object.hasOwn(current, part)) return undefined
        current = (current as Record<string, unknown>)[part]
    }
    return current
}

export function I18nProvider({ children, defaultLanguage = 'en' }: I18nProviderProps) {
    const [requested, setRequested] = useState(() => readPreferredLanguage(defaultLanguage))
    const [attempt, setAttempt] = useState(0)
    // Language, direction and dictionary move together. Initial non-English loads
    // show a localized loader; later changes retain the mounted page and its input.
    const [loaded, setLoaded] = useState(() => ({ language: requested, strings: cachedLanguage(requested) }))
    const [failed, setFailed] = useState(false)

    useEffect(() => installTypography(), [])
    useEffect(() => {
        let active = true
        loadLanguage(requested).then(strings => {
            if (!active) return
            setLoaded(current => current.language === requested && current.strings === strings
                ? current : { language: requested, strings })
            if (attempt > 0) {
                try { localStorage.setItem('dhtn-lang', requested) } catch { /* The active language still works in this tab. */ }
            }
        }).catch(error => {
            if (!active) return
            Sentry.captureException(error)
            setFailed(true)
        })
        return () => { active = false }
    }, [requested, attempt])

    useLayoutEffect(() => {
        document.documentElement.lang = loaded.language
        document.documentElement.dir = loaded.language === 'ar' ? 'rtl' : 'ltr'
        document.body.classList.toggle('lang-ar', loaded.language === 'ar')
    }, [loaded.language])

    const setLanguage = useCallback((language: LanguageCode) => {
        setFailed(false)
        setRequested(language)
        setAttempt(value => value + 1)
    }, [])

    const t = useCallback((key: string): string => {
        const value = (loaded.strings && lookup(loaded.strings, key)) ?? lookup(en, key)
        return value === undefined ? key : typeof value === 'string' ? value : JSON.stringify(value)
    }, [loaded.strings])
    const copy = systemCopy[requested]

    return <I18nContext.Provider value={{ language: loaded.language, setLanguage, t }}>
        {loaded.strings ? children : !failed ? <PublicRouteLoading pathname={window.location.pathname} /> : null}
        {failed && <div role="alert" lang={requested} dir={requested === 'ar' ? 'rtl' : 'ltr'}
            className={loaded.strings
                ? 'fixed bottom-6 inset-x-4 z-[110] mx-auto max-w-lg rounded-card border-2 border-[var(--dhtn-border-strong)] bg-[var(--dhtn-canvas)] p-5 text-[var(--dhtn-text)]'
                : 'flex min-h-[100svh] flex-col items-center justify-center gap-6 bg-[var(--dhtn-canvas)] px-6 text-center text-[var(--dhtn-text)]'}>
            <p className="text-lg">{copy.languageFailed}</p>
            <div className="mt-4 flex items-center justify-center gap-4">
                <Button size="sm" onClick={() => { setFailed(false); setAttempt(value => value + 1) }}>{copy.retry}</Button>
                {loaded.strings && <button type="button" onClick={() => setFailed(false)} className="min-h-[44px] rounded px-3 focus-visible:outline-2 focus-visible:outline-offset-4">{copy.dismiss}</button>}
            </div>
        </div>}
    </I18nContext.Provider>
}
